'use client';

import { c as _c } from "react/compiler-runtime";
import type { DirectPurchaseBinding, FundingQuoteReview } from '@morfi/onramp-sdk';
import { type AmountInputCodec, createAmountInputCodec, decimalToUnits, type FundingFeeEstimate, type FundingLimit, type FundingMethod, formatFiatUnits, formatFundingPreset, fundingMethodArtwork, MoneyError } from '@morfi/onramp-sdk';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedAmountInput } from '@/components/trade/animated-amount-input';
import { AnimatedButton } from '@/components/ui/animated-button';
import { ArrowDown01Icon } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { currencyParts } from '@/lib/currency/index';
import { showToast } from '@/lib/ui/toast';
import { FundingFees } from './funding-fees';
export interface FundingAmountProps {
  method?: FundingMethod;
  methodLabel?: string;
  methodArtwork?: ReactNode;
  feeDisclosure?: ReactNode;
  currencySymbol?: string;
  presets: readonly string[];
  presetLabel?(units: string): string;
  currency: string;
  decimals: number;
  estimate: FundingFeeEstimate | null;
  purchase?: DirectPurchaseBinding;
  purchaseReview?: FundingQuoteReview | null;
  limits?: readonly FundingLimit[];
  recovery?: ReactNode;
  primaryAction?: ReactNode;
  primaryActionVisible?: boolean;
  quoteLoading?: boolean;
  feeLoading?: boolean;
  quoteIndicative?: boolean;
  loginForQuote?: boolean;
  allowanceBlocked?: boolean;
  allowanceExceeded?: boolean;
  boundaryFeedback?: 'snackbar';
  amountBoundary?: {
    kind: 'minimum' | 'maximum';
    amountUnits: string;
  } | null;
  validationError?: 'payment_invalid_request' | 'quote_not_executable' | null;
  value: string;
  canContinue: boolean;
  pending?: boolean;
  onAmountChange?(units: string | null): void;
  onValueChange(value: string): void;
  onChangeMethod(): void;
  onContinue(units: string): void;
}
const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'decimal', '0', 'delete'] as const;

/** Amount entry only. The host advances to contact/quote review, never directly submits a payment. */
export function FundingAmount(t0) {
  const $ = _c(138);
  const {
    method,
    methodLabel,
    methodArtwork,
    feeDisclosure,
    currencySymbol,
    presets,
    presetLabel,
    currency,
    decimals,
    estimate,
    purchase,
    purchaseReview,
    value: text,
    limits,
    recovery,
    primaryAction,
    primaryActionVisible: t1,
    quoteLoading,
    feeLoading: t2,
    quoteIndicative: t3,
    loginForQuote: t4,
    allowanceBlocked,
    allowanceExceeded: t5,
    amountBoundary,
    boundaryFeedback: t6,
    validationError,
    canContinue,
    pending: t7,
    onAmountChange,
    onValueChange,
    onChangeMethod,
    onContinue
  } = t0;
  const primaryActionVisible = t1 === undefined ? true : t1;
  const feeLoading = t2 === undefined ? quoteLoading : t2;
  const quoteIndicative = t3 === undefined ? false : t3;
  const loginForQuote = t4 === undefined ? false : t4;
  const allowanceExceeded = t5 === undefined ? allowanceBlocked : t5;
  const boundaryFeedback = t6 === undefined ? "snackbar" : t6;
  const pending = t7 === undefined ? false : t7;
  const t = useTranslations("onramp");
  const quoteStatus = useTranslations("trade.status");
  const locale = useLocale();
  const codec = createAmountInputCodec(locale, decimals);
  const [touched, setTouched] = useState(false);
  const units = readUnits(codec, text);
  let symbol;
  let t8;
  let t9;
  if ($[0] !== currency || $[1] !== currencySymbol || $[2] !== locale) {
    const parts = currencyParts(locale, currency);
    symbol = currencySymbol ?? parts.find(_temp)?.value ?? currency;
    t8 = parts.findIndex(_temp2);
    t9 = parts.findIndex(_temp3);
    $[0] = currency;
    $[1] = currencySymbol;
    $[2] = locale;
    $[3] = symbol;
    $[4] = t8;
    $[5] = t9;
  } else {
    symbol = $[3];
    t8 = $[4];
    t9 = $[5];
  }
  const suffix = t8 > t9;
  const valid = units !== null && BigInt(units) > BigInt(0);
  const validationMessage = allowanceExceeded ? "amount_over_limit" : validationError ?? (touched && text !== "" && !valid ? "invalid_amount" : null);
  const announcedError = useRef(null);
  let t10;
  let t11;
  if ($[6] !== t || $[7] !== validationMessage) {
    t10 = () => {
      if (validationMessage && announcedError.current !== validationMessage) {
        showToast({
          id: TOAST_IDS.ONRAMP_AMOUNT_ERROR,
          message: validationMessage === "invalid_amount" ? t("invalid_amount") : t(validationMessage),
          variant: "error"
        });
      }
      announcedError.current = validationMessage;
    };
    t11 = [validationMessage, t];
    $[6] = t;
    $[7] = validationMessage;
    $[8] = t10;
    $[9] = t11;
  } else {
    t10 = $[8];
    t11 = $[9];
  }
  useEffect(t10, t11);
  const boundaryKind = amountBoundary?.kind;
  const boundaryUnits = amountBoundary?.amountUnits;
  let t12;
  let t13;
  if ($[10] !== boundaryFeedback || $[11] !== boundaryKind || $[12] !== boundaryUnits || $[13] !== currency || $[14] !== decimals || $[15] !== locale || $[16] !== t) {
    t12 = () => {
      if (boundaryFeedback !== "snackbar" || !boundaryKind || boundaryUnits === undefined) {
        return;
      }
      const timer = setTimeout(() => showToast({
        id: TOAST_IDS.ONRAMP_AMOUNT_ERROR,
        message: t(boundaryKind === "minimum" ? "amount_minimum" : "amount_maximum", {
          amount: formatFiatUnits(boundaryUnits, decimals, currency, locale)
        }),
        variant: "error"
      }), 800);
      return () => clearTimeout(timer);
    };
    t13 = [boundaryFeedback, boundaryKind, boundaryUnits, currency, decimals, locale, t];
    $[10] = boundaryFeedback;
    $[11] = boundaryKind;
    $[12] = boundaryUnits;
    $[13] = currency;
    $[14] = decimals;
    $[15] = locale;
    $[16] = t;
    $[17] = t12;
    $[18] = t13;
  } else {
    t12 = $[17];
    t13 = $[18];
  }
  useEffect(t12, t13);
  let t14;
  if ($[19] !== codec || $[20] !== onAmountChange || $[21] !== onValueChange) {
    t14 = function change(value) {
      onValueChange(value);
      onAmountChange?.(readUnits(codec, value));
    };
    $[19] = codec;
    $[20] = onAmountChange;
    $[21] = onValueChange;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const change = t14;
  let t15;
  if ($[23] !== amountBoundary || $[24] !== canContinue || $[25] !== onContinue || $[26] !== pending || $[27] !== quoteLoading || $[28] !== setTouched || $[29] !== units || $[30] !== valid || $[31] !== validationError) {
    t15 = function submit() {
      setTouched(true);
      if (valid && units !== null && canContinue && !pending && !quoteLoading && !validationError && !amountBoundary) {
        onContinue(units);
      }
    };
    $[23] = amountBoundary;
    $[24] = canContinue;
    $[25] = onContinue;
    $[26] = pending;
    $[27] = quoteLoading;
    $[28] = setTouched;
    $[29] = units;
    $[30] = valid;
    $[31] = validationError;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const submit = t15;
  let t16;
  if ($[33] !== method || $[34] !== methodArtwork) {
    t16 = methodArtwork ?? (method ? <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" priority={true} rounded="" imageClassName="object-contain" src={`/assets/onramp/method-${fundingMethodArtwork[method]}.png`} alt="" size="size-5" className="bg-transparent" /> : null);
    $[33] = method;
    $[34] = methodArtwork;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== method || $[37] !== methodLabel || $[38] !== t) {
    t17 = methodLabel ?? (method ? t(method) : "");
    $[36] = method;
    $[37] = methodLabel;
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== onChangeMethod || $[41] !== pending || $[42] !== t16 || $[43] !== t17) {
    t18 = <AnimatedButton type="button" variant="secondary" className="h-auto self-center gap-2 rounded-full px-3 py-1 text-caption font-medium text-foreground-primary" suffix={ArrowDown01Icon} onClick={onChangeMethod} disabled={pending}>{t16}{t17}</AnimatedButton>;
    $[40] = onChangeMethod;
    $[41] = pending;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t) {
    t19 = t("principal");
    $[45] = t;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  const t20 = suffix ? "" : symbol;
  const t21 = suffix ? symbol : "";
  let t22;
  if ($[47] !== setTouched) {
    t22 = () => setTouched(true);
    $[47] = setTouched;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  const t23 = Boolean(validationError) || touched && text !== "" && !valid;
  let t24;
  if ($[49] !== change || $[50] !== pending || $[51] !== submit || $[52] !== t19 || $[53] !== t20 || $[54] !== t21 || $[55] !== t22 || $[56] !== t23 || $[57] !== text) {
    t24 = <div dir="ltr"><AnimatedAmountInput inputLabel={t19} currencySymbol={t20} suffixSymbol={t21} localizedInput={true} showQuickAmounts={false} inputMode="none" value={text} disabled={pending} onChange={change} onBlur={t22} onSubmit={submit} ariaInvalid={t23} /></div>;
    $[49] = change;
    $[50] = pending;
    $[51] = submit;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = text;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== change || $[60] !== codec || $[61] !== currency || $[62] !== decimals || $[63] !== locale || $[64] !== pending || $[65] !== presetLabel || $[66] !== presets) {
    let t26;
    if ($[68] !== change || $[69] !== codec || $[70] !== currency || $[71] !== decimals || $[72] !== locale || $[73] !== pending || $[74] !== presetLabel) {
      t26 = value_0 => <AnimatedButton key={value_0} type="button" variant="secondary" size="sm" className="rounded-full" disabled={pending} onClick={() => change(codec.format(BigInt(value_0)))}>{presetLabel?.(value_0) ?? formatFundingPreset(value_0, decimals, currency, locale)}</AnimatedButton>;
      $[68] = change;
      $[69] = codec;
      $[70] = currency;
      $[71] = decimals;
      $[72] = locale;
      $[73] = pending;
      $[74] = presetLabel;
      $[75] = t26;
    } else {
      t26 = $[75];
    }
    t25 = presets.map(t26);
    $[59] = change;
    $[60] = codec;
    $[61] = currency;
    $[62] = decimals;
    $[63] = locale;
    $[64] = pending;
    $[65] = presetLabel;
    $[66] = presets;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[76] !== t25) {
    t26 = <div className="flex flex-wrap justify-center gap-2">{t25}</div>;
    $[76] = t25;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  let t27;
  if ($[78] !== t) {
    t27 = t("amount_keypad");
    $[78] = t;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  let t28;
  if ($[80] !== change || $[81] !== codec || $[82] !== decimals || $[83] !== pending || $[84] !== t || $[85] !== text) {
    t28 = keys.map(key => <AnimatedButton key={key} type="button" variant="tertiary" className="h-12 sm:h-13 text-heading-lg font-medium text-foreground-primary" disabled={pending || key === "decimal" && decimals === 0} aria-label={key === "delete" ? t("delete_digit") : key === "decimal" ? t("decimal_point") : t("digit", {
      digit: key
    })} onClick={() => change(codec.key(text, key))}>{key === "delete" ? "\u232B" : key === "decimal" ? codec.decimalSeparator : codec.format(decimalToUnits(key, decimals))}</AnimatedButton>);
    $[80] = change;
    $[81] = codec;
    $[82] = decimals;
    $[83] = pending;
    $[84] = t;
    $[85] = text;
    $[86] = t28;
  } else {
    t28 = $[86];
  }
  let t29;
  if ($[87] !== t27 || $[88] !== t28) {
    t29 = <fieldset aria-label={t27} className="grid grid-cols-3 gap-0 my-1">{t28}</fieldset>;
    $[87] = t27;
    $[88] = t28;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] !== currency || $[91] !== decimals || $[92] !== estimate || $[93] !== feeDisclosure || $[94] !== feeLoading || $[95] !== limits || $[96] !== purchase || $[97] !== purchaseReview || $[98] !== quoteIndicative || $[99] !== units) {
    t30 = feeDisclosure ?? <FundingFees currency={currency} decimals={decimals} principalUnits={units} estimate={estimate} indicative={quoteIndicative} {...purchase ? {
      purchase,
      purchaseReview
    } : {}} {...limits ? {
      limits
    } : {}} {...feeLoading !== undefined ? {
      loading: feeLoading
    } : {}} />;
    $[90] = currency;
    $[91] = decimals;
    $[92] = estimate;
    $[93] = feeDisclosure;
    $[94] = feeLoading;
    $[95] = limits;
    $[96] = purchase;
    $[97] = purchaseReview;
    $[98] = quoteIndicative;
    $[99] = units;
    $[100] = t30;
  } else {
    t30 = $[100];
  }
  let t31;
  if ($[101] !== allowanceBlocked || $[102] !== amountBoundary || $[103] !== boundaryFeedback || $[104] !== canContinue || $[105] !== currency || $[106] !== decimals || $[107] !== locale || $[108] !== loginForQuote || $[109] !== pending || $[110] !== primaryAction || $[111] !== primaryActionVisible || $[112] !== quoteLoading || $[113] !== quoteStatus || $[114] !== submit || $[115] !== t || $[116] !== valid || $[117] !== validationError) {
    t31 = !primaryAction || !primaryActionVisible ? <AnimatedButton type="button" className="h-11 w-full" size="lg" disabled={Boolean(allowanceBlocked) || Boolean(amountBoundary) || !valid || !canContinue || Boolean(validationError)} loading={pending || quoteLoading} onClick={submit}>{allowanceBlocked ? t("amount_over_limit") : boundaryFeedback !== "snackbar" && amountBoundary?.kind === "minimum" ? t("below_minimum", {
        amount: formatFiatUnits(amountBoundary.amountUnits, decimals, currency, locale)
      }) : boundaryFeedback !== "snackbar" && amountBoundary?.kind === "maximum" ? t("above_maximum", {
        amount: formatFiatUnits(amountBoundary.amountUnits, decimals, currency, locale)
      }) : quoteLoading ? quoteStatus("loading_quote") : pending ? t("preparing_payment") : !valid ? t("enter_amount") : loginForQuote ? t("login_for_quote") : canContinue ? t("continue") : t("review_amount")}</AnimatedButton> : null;
    $[101] = allowanceBlocked;
    $[102] = amountBoundary;
    $[103] = boundaryFeedback;
    $[104] = canContinue;
    $[105] = currency;
    $[106] = decimals;
    $[107] = locale;
    $[108] = loginForQuote;
    $[109] = pending;
    $[110] = primaryAction;
    $[111] = primaryActionVisible;
    $[112] = quoteLoading;
    $[113] = quoteStatus;
    $[114] = submit;
    $[115] = t;
    $[116] = valid;
    $[117] = validationError;
    $[118] = t31;
  } else {
    t31 = $[118];
  }
  const t32 = !primaryActionVisible;
  const t33 = !primaryActionVisible;
  let t34;
  if ($[119] !== primaryAction || $[120] !== t32 || $[121] !== t33) {
    t34 = <div hidden={t32} inert={t33}>{primaryAction}</div>;
    $[119] = primaryAction;
    $[120] = t32;
    $[121] = t33;
    $[122] = t34;
  } else {
    t34 = $[122];
  }
  let t35;
  if ($[123] !== t31 || $[124] !== t34) {
    t35 = <div className="relative">{t31}{t34}</div>;
    $[123] = t31;
    $[124] = t34;
    $[125] = t35;
  } else {
    t35 = $[125];
  }
  let t36;
  if ($[126] !== method || $[127] !== t) {
    t36 = method === "apple_pay" ? <p className="text-center text-caption text-foreground-primary">{t("apple_debit_tip")}</p> : null;
    $[126] = method;
    $[127] = t;
    $[128] = t36;
  } else {
    t36 = $[128];
  }
  let t37;
  if ($[129] !== recovery || $[130] !== t18 || $[131] !== t24 || $[132] !== t26 || $[133] !== t29 || $[134] !== t30 || $[135] !== t35 || $[136] !== t36) {
    t37 = <div className="flex flex-col gap-3">{t18}{t24}{t26}{t29}{t30}{recovery}{t35}{t36}</div>;
    $[129] = recovery;
    $[130] = t18;
    $[131] = t24;
    $[132] = t26;
    $[133] = t29;
    $[134] = t30;
    $[135] = t35;
    $[136] = t36;
    $[137] = t37;
  } else {
    t37 = $[137];
  }
  return t37;
}
function _temp3(part_1) {
  return part_1.type === "integer";
}
function _temp2(part_0) {
  return part_0.type === "currency";
}
function _temp(part) {
  return part.type === "currency";
}
function readUnits(codec: AmountInputCodec, value: string): string | null {
  try {
    return codec.parse(value, BigInt('99999999999999999999')).toString();
  } catch (error) {
    // SILENT-CATCH: Incomplete or invalid typed amounts use validation feedback, not operational failures.
    if (error instanceof MoneyError) return null;
    throw error;
  }
}